import {observer} from 'mobx-react-lite'
import React, {useState} from 'react'
import {Lang} from 'yougile-lang'
import {Dialog, DialogProps} from '@components/common/base/dialog/dialog'
import {LinkButton} from '../buttons'
import {NotificationPopup} from './notification-popup'
import {
  NotificationsContext,
  NotificationsStore,
  useNotificationsStore
} from './notifications-store'
import {useKeyboardInteraction} from './use-keyboard-interaction'

export const notificationsStoreInstanceRef = {
  current: null as NotificationsStore | null
}

export type MessageParts = {
  title: string
  subtitle?: string
}
type Message = string | MessageParts

export const showConfirm = async (
  message: Message,
  noShowMoreKey?: string | false,
  newDialog?: boolean,
  dialogProps?: Partial<Omit<DialogProps, 'noShowMore' | 'noShowMoreTextCb'>>
): Promise<boolean> => {
  const notificationsStore = notificationsStoreInstanceRef.current
  if (!notificationsStore) {
    return Promise.resolve(false)
  }

  const {title, subtitle} =
    typeof message === 'string' ? ({title: message} as MessageParts) : message

  return notificationsStore.showConfirm({
    title,
    subtitle,
    noShowMoreKey,
    newDialog,
    dialogProps
  })
}

export const showDeleteConfirm = (
  message: Message,
  noShowMoreKey?: string | false,
  dialogProps?: Partial<Omit<DialogProps, 'noShowMore' | 'noShowMoreTextCb'>>
) => {
  return showConfirm(message, noShowMoreKey, true, {
    isDelete: true,
    primaryBtnText: Lang.msg.delete,
    ...dialogProps
  })
}

interface Props {
  message: string
  ok(): void
  close(): void
}

const Confirm = (props: Props) => {
  const {message, ok, close} = props

  useKeyboardInteraction({ok, close})

  return (
    <NotificationPopup
      title={Lang.msg.common.confirm}
      icon="IconAlertCircle"
      content={<div className="text-secondary">{message}</div>}
      onClose={close}
      buttons={
        <>
          <LinkButton size="small" onClick={ok}>
            {Lang.msg.ok}
          </LinkButton>
          <LinkButton color="secondary" size="small" onClick={close}>
            {Lang.msg.cancel}
          </LinkButton>
        </>
      }
    />
  )
}

const ConfirmManagerInner = observer(() => {
  const [state, store] = useNotificationsStore()
  const firstNotification = state.notifications[0]
  if (!firstNotification) {
    return null
  }

  const {title, subtitle, dialogProps, newDialog} = firstNotification
  if (!newDialog) {
    return (
      <Confirm
        message={title}
        ok={() => store.resolveNotification(firstNotification, true)}
        close={() => store.resolveNotification(firstNotification, false)}
      />
    )
  }

  const dialogPropsExtended: DialogProps = {
    title,
    subtitle,
    close: () => store.resolveNotification(firstNotification, false),
    submit: () => store.resolveNotification(firstNotification, true),
    ...dialogProps
  }

  if (firstNotification.noShowMoreKey) {
    dialogPropsExtended.noShowMore = true
    dialogPropsExtended.noShowMoreTextCb = (value: boolean) => {
      store.setNoShowMoreValue(firstNotification, value)
    }
  }

  return <Dialog {...dialogPropsExtended} />
})

export const ConfirmManager = () => {
  const [notificationsStore] = useState(() => new NotificationsStore())
  notificationsStoreInstanceRef.current = notificationsStore
  return (
    <NotificationsContext.Provider value={notificationsStore}>
      <ConfirmManagerInner />
    </NotificationsContext.Provider>
  )
}
